// btw-front/src/components/admin/DealChangeHistory.jsx
//
// 관리자 전용 — 딜 수정 이력 표시.
// GET /api/admin/deals/:id/changes 응답을 시간 역순으로 나열.
// 파트너 PUT·관리자 PUT 모두 기록됨 — actorType('partner'/'admin')으로 변경자 구분 배지 표시.
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { listChanges } from "../../api/adminDeals";
import { Badge } from "../ui/Badge";

function formatKRW(n) {
  return new Intl.NumberFormat("ko-KR").format(Number(n) || 0);
}

// 컬럼명 → i18n 키. 누락 컬럼은 컬럼명 그대로 표시.
const COLUMN_LABEL_KEY = {
  project_name: "partners.deals.form.projectName",
  customer_company: "partners.deals.form.customerCompany",
  customer_contact_name: "partners.deals.form.customerContactName",
  customer_contact_position: "partners.deals.form.customerContactPosition",
  customer_phone: "partners.deals.form.customerPhone",
  customer_email: "partners.deals.form.customerEmail",
  sales_rep_name: "partners.deals.form.salesRepName",
  sales_rep_phone: "partners.deals.form.salesRepPhone",
  sales_rep_email: "partners.deals.form.salesRepEmail",
  partner_sales_rep_id: "partners.deals.form.salesRepName",
  po_expected_quarter: "partners.deals.form.poQuarter",
  po_expected_date: "partners.deals.form.poDate",
  product_detail: "partners.deals.form.productDetail",
  po_amount: "partners.deals.form.poAmount",
  vat_excluded: "partners.deals.form.vatExcluded",
  sales_progress: "partners.deals.form.salesProgress",
  channel: "partners.deals.form.channel",
  poc_required: "partners.deals.form.poc",
  brochure_delivered: "partners.deals.form.brochure",
  status: "partners.deals.detail.statusLabel",
  partner_memo: "partners.deals.detail.partnerMemoLabel",
  // 심사 상태 — 영업단계(status)와 별개 축. 이 화면은 파트너 딜 상세에서도 쓰이므로
  // 라벨이 없으면 파트너에게 컬럼명(review_status)이 그대로 노출된다.
  review_status: "partners.deals.review.label",
  // 담당자 변경 — id 대신 이력에 함께 남긴 이름을 보여 준다(아래 HIDDEN_COLUMNS)
  partner_company_name: "admin.deals.changeHistory.partnerCompany",
  owner_name: "admin.deals.changeHistory.ownerAccount",
};

// 담당자 변경 이력의 id 컬럼 — 같은 이력에 이름 키가 함께 있어 숫자 id 는 감춘다
const HIDDEN_COLUMNS = new Set(["partner_id", "owner_user_id", "admin_sales_rep_id"]);

// 컬럼별 값 라벨 변환 — ENUM·boolean·금액·긴 텍스트 등.
function formatValue(t, column, value) {
  if (value === null || value === undefined || value === "") return "-";
  switch (column) {
    case "status":
      return t(`partners.deals.status.${value}`, { defaultValue: String(value) });
    case "review_status":
      return t(`partners.deals.review.${value}`, { defaultValue: String(value) });
    case "poc_required":
      if (value === "required") return t("partners.deals.form.pocRequired");
      if (value === "not_required") return t("partners.deals.form.pocNotRequired");
      return t("partners.deals.form.pocUndecided");
    case "vat_excluded":
    case "brochure_delivered": {
      const bool = value === 1 || value === "1" || value === true || value === "true";
      return bool
        ? t("admin.deals.changeHistory.boolTrue")
        : t("admin.deals.changeHistory.boolFalse");
    }
    case "po_amount":
      return `${formatKRW(value)} ${t("common.unitKrw")}`;
    case "sales_progress":
    case "product_detail":
    case "partner_memo": {
      const s = String(value);
      return s.length > 60 ? `${s.slice(0, 60)}…` : s;
    }
    default:
      return String(value);
  }
}

// 변경 전/후 단일 값 표시 — 영업단계(status)는 Badge, 그 외는 텍스트.
// after 는 강조(진하게), before 는 흐리게 + 취소선으로 "바뀌기 전" 임을 시각화.
function ChangeValue({ t, col, value, side }) {
  const empty = value === null || value === undefined || value === "";
  const display = formatValue(t, col, value);
  if (col === "status" && !empty) {
    return <Badge tone={String(value)}>{display}</Badge>;
  }
  const cls =
    side === "after"
      ? "font-medium text-slate-900"
      : "text-slate-400 line-through decoration-slate-300";
  return <span className={cls}>{display}</span>;
}

export default function DealChangeHistory({ dealId }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listChanges(dealId)
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dealId]);

  if (loading) {
    return <p className="text-sm text-slate-400">{t("common.loading")}</p>;
  }
  if (!items.length) {
    return <p className="text-sm text-slate-500">{t("admin.deals.changeHistory.empty")}</p>;
  }

  return (
    <ol className="space-y-0">
      {items.map((entry, idx) => {
        const last = idx === items.length - 1;
        return (
          <li key={entry.id} className="flex gap-3">
            {/* 타임라인 레일 — 도트 + 다음 항목으로 잇는 세로선 */}
            <div className="flex flex-col items-center" aria-hidden="true">
              <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-accent ring-4 ring-accent/10" />
              {!last && <span className="w-px flex-1 bg-slate-200" />}
            </div>

            {/* 본문 — 마지막 항목 외엔 아래 여백으로 세로선 길이 확보 */}
            <div className={`min-w-0 flex-1 ${last ? "" : "pb-5"}`}>
              {/* 헤더: 시각 · 변경자 · (파트너) */}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <time
                  dateTime={
                    entry.createdAt
                      ? String(entry.createdAt).slice(0, 16).replace(" ", "T")
                      : undefined
                  }
                  className="text-xs font-medium tabular-nums text-slate-700"
                >
                  {/* 분 단위까지만 표시(초 생략) — 정렬은 created_at DESC, id DESC 로 보존 */}
                  {entry.createdAt ? String(entry.createdAt).slice(0, 16).replace("T", " ") : ""}
                </time>
                <span className="text-slate-300" aria-hidden="true">
                  ·
                </span>
                <span className="text-xs text-slate-600">
                  {entry.changedByName || t("admin.deals.changeHistory.unknownUser")}
                </span>
                {/* 변경자 구분 — 관리자는 accent 톤으로 한눈에 식별 */}
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                    entry.actorType === "admin"
                      ? "bg-accent/10 text-accent"
                      : "bg-slate-100 text-slate-500"
                  }`}
                >
                  {t(
                    entry.actorType === "admin"
                      ? "admin.deals.changeHistory.adminActor"
                      : "admin.deals.changeHistory.partnerActor",
                  )}
                </span>
              </div>

              {/* 변경 필드: 라벨(고정폭) + 이전값 → 이후값 */}
              <ul className="mt-2 space-y-1.5">
                {(entry.changedFields || [])
                  .filter((col) => !HIDDEN_COLUMNS.has(col))
                  .map((col) => {
                    const labelKey = COLUMN_LABEL_KEY[col];
                    const label = labelKey ? t(labelKey) : col;
                    return (
                      <li key={col} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                        <span className="w-28 shrink-0 text-slate-500">{label}</span>
                        <ChangeValue t={t} col={col} value={entry.before?.[col]} side="before" />
                        <span className="text-slate-300" aria-hidden="true">
                          →
                        </span>
                        <ChangeValue t={t} col={col} value={entry.after?.[col]} side="after" />
                      </li>
                    );
                  })}
              </ul>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
